// src/components/health/PrescriptionProcessingInfo.tsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FileText, ShieldCheck, Search,
  Pill, Microscope
} from 'lucide-react';

interface PrescriptionProcessingInfoProps {
  fileName: string;
  status: 'processing' | 'completed' | 'failed';
  variant?: 'fullscreen' | 'embedded';
  errorMessage?: string;
  errorCode?: string;
  errorType?: string;
  onRetry?: () => void;
  onComplete?: () => void;
}

const PRESCRIPTION_TIPS = [
  {
    title: "While we process...",
    text: "Did you know? heal extracts medicine names, dosages, and frequencies from your prescriptions to help you stay on track."
  },
  {
    title: "Medicine Safety",
    text: "We organize your medications by category and provide detailed explanations to help you understand what you're taking."
  },
  {
    title: "Smart Extraction",
    text: "heal can read both handwritten and printed prescriptions, making it easy to digitize all your medical records."
  },
  {
    title: "Dosage Tracking",
    text: "Never miss a dose! heal keeps track of your medication schedule and helps you follow your doctor's instructions."
  },
  {
    title: "Medicine Info",
    text: "Tap any medicine to learn more about it - what it does, how it works, and why your doctor prescribed it."
  },
  {
    title: "Health History",
    text: "All your prescriptions are saved in one place, making it easy to share your medical history with healthcare providers."
  }
];

export const PrescriptionProcessingInfo: React.FC<PrescriptionProcessingInfoProps> = ({
  fileName,
  status,
  variant = 'fullscreen',
  errorMessage,
  errorCode,
  errorType,
  onRetry,
  onComplete
}) => {
  const [progress, setProgress] = useState(0);
  const [tipIndex, setTipIndex] = useState(0);

  // Fake progress simulation
  useEffect(() => {
    if (status === 'processing') {
      let currentProgress = 0;
      const interval = setInterval(() => {
        setProgress(prev => {
          currentProgress = prev;

          // Fast progress to 60%
          if (currentProgress < 60) {
            return Math.min(currentProgress + Math.random() * 8 + 4, 60);
          }
          // Slow crawl to 90%
          else if (currentProgress < 90) {
            return Math.min(currentProgress + Math.random() * 2 + 0.5, 90);
          }
          // Stay at 90% until success
          else {
            return 90;
          }
        });
      }, 300);

      return () => clearInterval(interval);
    } else if (status === 'completed') {
      // Snap to 100% on completion
      setProgress(100);

      // Wait a moment before calling onComplete
      const timeout = setTimeout(() => {
        if (onComplete) {
          onComplete();
        }
      }, 1500);

      return () => clearTimeout(timeout);
    }
  }, [status, onComplete]);

  // Rotate tips every 8 seconds
  useEffect(() => {
    if (status === 'processing') {
      const tipInterval = setInterval(() => {
        setTipIndex(prev => (prev + 1) % PRESCRIPTION_TIPS.length);
      }, 8000);
      return () => clearInterval(tipInterval);
    }
  }, [status]);

  // Determine current icon based on progress
  const getCurrentIcon = () => {
    const iconStyle = { width: '32px', height: '32px', color: '#000000' };
    if (progress < 20) return <ShieldCheck style={iconStyle} />;
    if (progress < 50) return <Search style={iconStyle} />;
    if (progress < 80) return <FileText style={iconStyle} />;
    return <Pill style={iconStyle} />;
  };

  const getMainStatus = () => {
    if (status === 'completed') return 'Extraction Complete';
    if (status === 'failed') return 'Processing Failed';
    if (progress < 20) return "Securing Environment";
    if (progress < 50) return "Reading Prescription";
    if (progress < 80) return "Extracting Medicines";
    return "Organizing Data";
  };

  // Card content (reusable for both variants)
  const cardContent = (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      style={{
        backgroundColor: '#ffffff',
        borderRadius: variant === 'embedded' ? '8px' : '12px',
        boxShadow: variant === 'embedded' ? 'none' : '0 20px 60px rgba(0, 0, 0, 0.15)',
        border: '1px solid #e5e7eb',
        overflow: 'hidden',
        position: 'relative'
      }}
    >
      {/* TOP ACCENT - Solid Black Line */}
      <div style={{ height: '6px', width: '100%', backgroundColor: '#000000' }} />

      {/* HEADER */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '20px 32px',
        borderBottom: '1px solid #f3f4f6'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* Black Pulse Dot */}
          <div style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: '#000000',
            animation: 'pulse 2s ease-in-out infinite'
          }} />
          <span style={{
            fontSize: '10px',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.15em',
            color: '#111827'
          }}>
            heal Intelligent Scan
          </span>
        </div>
        <span style={{
          fontSize: '9px',
          textTransform: 'uppercase',
          fontWeight: 600,
          letterSpacing: '0.1em',
          color: '#6b7280'
        }}>
          {fileName.length > 20 ? fileName.substring(0, 20) + '...' : fileName}
        </span>
      </div>

      {/* MAIN VISUAL AREA */}
      <div style={{
        padding: '40px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        minHeight: '320px'
      }}>

        {/* BACKGROUND - Clean White/Gray Radial */}
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(circle at center, #f9fafb, #ffffff, #ffffff)'
        }} />

        {/* CENTRAL ICON WITH PROGRESS RING */}
        <div style={{ position: 'relative', marginBottom: '40px' }}>
          {/* The Rotating Ring - Gray Track */}
          <svg style={{ width: '128px', height: '128px', transform: 'rotate(-90deg)' }}>
            <circle
              cx="64" cy="64" r="60"
              stroke="currentColor" strokeWidth="1" fill="transparent"
              style={{ color: '#e5e7eb' }}
            />
            {/* Progress Fill - Solid Black */}
            <motion.circle
              cx="64" cy="64" r="60"
              stroke="currentColor" strokeWidth="2" fill="transparent"
              style={{ color: '#000000' }}
              strokeDasharray="377"
              strokeDashoffset={377 - (377 * progress) / 100}
              strokeLinecap="round"
              initial={{ strokeDashoffset: 377 }}
              animate={{ strokeDashoffset: 377 - (377 * progress) / 100 }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
            />
          </svg>

          {/* The Icon inside */}
          <div style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <motion.div
              key={getMainStatus()}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: "spring", bounce: 0.5 }}
              style={{
                backgroundColor: '#ffffff',
                padding: '20px',
                borderRadius: '50%',
                border: '1px solid #f3f4f6',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                zIndex: 10
              }}
            >
              {getCurrentIcon()}
            </motion.div>
          </div>

          {/* The Scanning Beam - Black Shadow/Light */}
          {status === 'processing' && (
            <motion.div
              animate={{ top: ["0%", "100%", "0%"], opacity: [0, 1, 0] }}
              transition={{ repeat: Infinity, duration: 2.5, ease: "linear" }}
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                height: '2px',
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                boxShadow: '0 0 20px rgba(0, 0, 0, 0.2)',
                filter: 'blur(0.5px)',
                zIndex: 20,
                width: '100%'
              }}
            />
          )}
        </div>

        {/* STATUS TEXT */}
        <div style={{ textAlign: 'center', zIndex: 10 }}>
          <motion.h2
            key={getMainStatus()}
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
              fontSize: '24px',
              fontWeight: 700,
              color: '#111827',
              letterSpacing: '-0.025em',
              marginBottom: '8px'
            }}
          >
            {getMainStatus()}
          </motion.h2>

          {/* Progress percentage */}
          {status === 'processing' && (
            <div style={{ height: '24px', overflow: 'hidden', position: 'relative' }}>
              <motion.p
                key={Math.floor(progress / 10)}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                style={{
                  fontSize: '12px',
                  fontFamily: '"Space Mono", monospace',
                  color: '#9ca3af'
                }}
              >
                {`> ${Math.floor(progress)}% complete`}
              </motion.p>
            </div>
          )}

          {/* Completion Message */}
          {status === 'completed' && (
            <p style={{
              fontSize: '14px',
              color: '#10b981',
              marginTop: '16px'
            }}>
              Your prescription has been successfully extracted!
            </p>
          )}

          {/* Processing Failure or Quota Message */}
          {status === 'failed' && (
            <div style={{ marginTop: '16px', maxWidth: '400px', textAlign: 'center' }}>
              {errorCode === 'QUOTA_EXCEEDED' ? (
                <>
                  <p style={{
                    fontSize: '16px',
                    fontWeight: 600,
                    color: '#111827',
                    marginBottom: '8px'
                  }}>
                    Daily Limit Reached
                  </p>
                  <p style={{
                    fontSize: '14px',
                    color: '#6b7280',
                    lineHeight: '1.6',
                    marginBottom: '16px'
                  }}>
                    {errorMessage && !errorMessage.includes('quota') ? errorMessage : "Hey sorry, looks like a lot of people processed a lot of documents today and we've hit our quota for today. We're sorry, we're still a small operation and would request you to come back tomorrow!"}
                  </p>
                  {onRetry && (
                    <button
                      onClick={onRetry}
                      style={{
                        padding: '12px 24px',
                        backgroundColor: '#000000',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#1f2937';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#000000';
                      }}
                    >
                      Back to Dashboard
                    </button>
                  )}
                </>
              ) : (
                <>
                  <p style={{
                    fontSize: '16px',
                    fontWeight: 600,
                    color: '#ef4444',
                    marginBottom: '8px'
                  }}>
                    Processing Failed
                  </p>
                  <p style={{
                    fontSize: '14px',
                    color: '#6b7280',
                    lineHeight: '1.6',
                    marginBottom: '16px'
                  }}>
                    {errorMessage || 'Something went wrong while processing your prescription. Please try again.'}
                  </p>
                  {onRetry && (
                    <button
                      onClick={onRetry}
                      style={{
                        padding: '12px 24px',
                        backgroundColor: '#000000',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: 600,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#1f2937';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#000000';
                      }}
                    >
                      Back to Dashboard
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* BOTTOM SECTION: EDUCATIONAL CARD */}
      {status === 'processing' && (
        <div style={{
          backgroundColor: '#f9fafb',
          borderTop: '1px solid #f3f4f6',
          padding: '32px',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
            <div style={{ flexShrink: 0, marginTop: '4px' }}>
              <Pill size={20} style={{ color: '#000000' }} />
            </div>

            <div style={{ position: 'relative', width: '100%', minHeight: '96px' }}>
              <AnimatePresence mode='wait'>
                <motion.div
                  key={tipIndex}
                  initial={{ opacity: 0, x: 10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  transition={{ duration: 0.4 }}
                  style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <h3 style={{
                    fontSize: '10px',
                    fontWeight: 700,
                    color: '#111827',
                    textTransform: 'uppercase',
                    letterSpacing: '0.15em',
                    marginBottom: '8px'
                  }}>
                    {PRESCRIPTION_TIPS[tipIndex].title}
                  </h3>
                  <p style={{
                    fontSize: '14px',
                    color: '#4b5563',
                    lineHeight: '1.6',
                    fontWeight: 500,
                    margin: 0
                  }}>
                    {PRESCRIPTION_TIPS[tipIndex].text}
                  </p>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          {/* Subtle Progress Bar - Black Line */}
          <motion.div
            key={tipIndex}
            initial={{ width: "0%" }}
            animate={{ width: "100%" }}
            transition={{ duration: 8, ease: "linear" }}
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              height: '4px',
              backgroundColor: '#000000'
            }}
          />
        </div>
      )}

    </motion.div>
  );

  // Embedded variant - return just the card
  if (variant === 'embedded') {
    return (
      <>
        {cardContent}
        <style>{`
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
        `}</style>
      </>
    );
  }

  // Fullscreen variant - return with wrapper
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgba(0, 0, 0, 0.75)',
      backdropFilter: 'blur(8px)',
      padding: '24px',
      fontFamily: 'var(--font-primary)',
      zIndex: 9999
    }}>
      <div style={{ width: '100%', maxWidth: '480px' }}>
        {cardContent}

        {/* Processing indicator */}
        {status === 'processing' && (
          <p style={{
            textAlign: 'center',
            color: '#6b7280',
            fontSize: '10px',
            textTransform: 'uppercase',
            letterSpacing: '0.15em',
            marginTop: '24px',
            fontWeight: 600,
            animation: 'pulse 2s ease-in-out infinite'
          }}>
            Processing prescription...
          </p>
        )}
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
};
